import { useState, useEffect } from "react";

// Official PCM colors from the color palette
const PCM_CONFIG = {
  Analyseur: { 
    bg: "#32A2FF", 
    text: "#FFFFFF",
    keywords: "Logique, Responsable, Organisé"
  },
  Empathique: { 
    bg: "#FC7403", 
    text: "#FFFFFF",
    keywords: "Compatissant, Sensible, Chaleureux"
  },
  Persévérant: { 
    bg: "#7F379D", 
    text: "#FFFFFF",
    keywords: "Engagé, Observateur, Consciencieux"
  },
  Énergiseur: { 
    bg: "#FAE200", 
    text: "#333333",
    keywords: "Spontané, Créatif, Ludique"
  },
  Promoteur: { 
    bg: "#FF1933", 
    text: "#FFFFFF",
    keywords: "Adaptable, Persuasif, Charmeur"
  },
  Imagineur: { 
    bg: "#E3A88D", 
    text: "#FFFFFF",
    keywords: "Imaginatif, Réfléchi, Calme"
  },
};

/**
 * PCMBuilding - Displays the PCM personality building visualization (compact version)
 * @param {Object} pcmFloors - Object with PCM types as keys and percentages as values
 * @param {string} pcmBase - The base PCM type (always at bottom, 100%)
 * @param {string} pcmPhase - The current phase PCM type
 */
const PCMBuilding = ({ pcmFloors, pcmBase, pcmPhase }) => {
  const [sortedFloors, setSortedFloors] = useState([]);

  useEffect(() => {
    if (!pcmFloors || Object.keys(pcmFloors).length === 0) {
      setSortedFloors([]);
      return;
    }

    let floors = Object.entries(pcmFloors).map(([type, percent]) => ({
      type,
      percent: percent || 0,
      isBase: type === pcmBase,
      isPhase: type === pcmPhase,
    }));

    // Sort by percentage ascending (smallest at top, largest at bottom)
    // Base is always at the very bottom
    floors.sort((a, b) => {
      if (a.isBase) return 1;
      if (b.isBase) return -1;
      return a.percent - b.percent;
    });

    setSortedFloors(floors);
  }, [pcmFloors, pcmBase, pcmPhase]);

  if (!pcmFloors || Object.keys(pcmFloors).length === 0) {
    return (
      <div className="flex items-center justify-center p-4 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
        <p className="text-sm text-slate-400">Profil PCM non configuré</p>
      </div>
    );
  }

  const maxPercent = 100;

  return (
    <div className="relative" data-testid="pcm-building">
      {/* House shape container - compact */}
      <div className="relative">
        {/* Roof - smaller */}
        <div className="relative mx-auto" style={{ width: "85%" }}>
          <svg viewBox="0 0 200 25" className="w-full h-auto">
            <polygon 
              points="100,2 198,25 2,25" 
              fill="#f1f5f9" 
              stroke="#e2e8f0" 
              strokeWidth="1"
            />
          </svg>
        </div>
        
        {/* Building body - compact */}
        <div className="relative bg-slate-100 rounded-b-lg border border-t-0 border-slate-200 flex">
          {/* Vertical arrow on the left - shorter */}
          <div className="flex flex-col items-center px-2 py-2">
            <div className="w-0.5 h-full bg-[#101E4D] relative min-h-[180px]">
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-b-[6px] border-l-transparent border-r-transparent border-b-[#101E4D]" />
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4px] border-r-[4px] border-t-[6px] border-l-transparent border-r-transparent border-t-[#101E4D]" />
            </div>
          </div>

          {/* Floors - compact spacing */}
          <div className="flex-1 py-2 pr-3 space-y-1.5">
            {sortedFloors.map((floor) => {
              const config = PCM_CONFIG[floor.type] || { bg: "#94A3B8", text: "#FFFFFF", keywords: "" };
              const widthPercent = Math.max((floor.percent / maxPercent) * 100, 20);
              
              return (
                <div
                  key={floor.type}
                  className="relative"
                  data-testid={`pcm-floor-${floor.type}`}
                >
                  {/* Type name and badge - smaller text */}
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-bold text-[#101E4D] uppercase tracking-wide">
                      {floor.type}
                    </span>
                    {(floor.isBase || floor.isPhase) && (
                      <span className="text-xs font-semibold text-[#101E4D]">
                        {floor.isBase ? "Base" : "Phase"}
                      </span>
                    )}
                  </div>
                  
                  {/* Colored bar - shorter height */}
                  <div className="relative h-5 bg-white rounded overflow-hidden">
                    <div
                      className="h-full rounded flex items-center justify-end px-2 transition-all duration-500"
                      style={{ 
                        backgroundColor: config.bg,
                        width: `${widthPercent}%`,
                        minWidth: "40px"
                      }}
                    >
                      <span 
                        className="text-xs font-bold"
                        style={{ color: config.text }}
                      >
                        {floor.percent}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PCMBuilding;
